// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldSearchSelect, FieldText, Segmented, Toggle } from "components/AtomsV2";
import { BrandLogo, Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  openSuccessAlert,
  selectThemeMode,
  setThemeMode,
} from "state/application/applicationSlice";
import {
  selectAllPermissions,
  selectAllUsers,
  useCreateCommunityAdminMutation,
  useCreateSuperUserMutation,
  useGetAllCommunitiesQuery,
  useGetAllPermissionsQuery,
  useGetAllUsersQuery,
  useGetEMSModulesQuery,
  useUpdatePermissionsByIdMutation,
} from "state/admin/adminApiSlice";

import ReferenceDataSection from "./ReferenceDataSection";
import UsersSection from "./UsersSection";

const MODULE_CONFIG = {
  OPTIMIZATION_MODULE: { icon: "tune", color: "#17C1E8" },
  SIMULATION_MODULE: { icon: "science", color: "#7928CA" },
  SIZING_MODULE: { icon: "straighten", color: "#FF9800" },
  PRICING_MODULE: { icon: "euro", color: "#00D084" },
};

const EMS_COLORS = ["#17C1E8", "#00B4D8", "#06D6A0", "#CB3066", "#FF9800", "#7928CA"];

const CREATE_TYPES = {
  ADMIN: "ADMIN",
  SUPER_USER: "SUPER_USER",
};

const TABS = [
  { id: "users", icon: "group", labelKey: "tabs.users" },
  { id: "data", icon: "database", labelKey: "tabs.data" },
];

/* Permission toggle row (design's PermRow): status dot + icon + label + switch */
function PermRow({ label, icon, color, on, onToggle, disabled }) {
  return (
    <div
      className="flex items-center justify-between cursor-pointer"
      style={{ padding: "9px 12px", opacity: disabled ? 0.55 : 1 }}
      role="presentation"
      onClick={disabled ? undefined : onToggle}
    >
      <div className="flex items-center gap-[10px] min-w-0">
        <span
          className="shrink-0"
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: on ? color : "var(--border-strong)",
          }}
        />
        <Icon name={icon} style={{ fontSize: 17, color: on ? color : "var(--ink-3)" }} />
        <span
          className="text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ fontWeight: on ? 600 : 500, color: on ? "var(--ink)" : "var(--ink-3)" }}
        >
          {label}
        </span>
      </div>
      <div className="shrink-0" style={{ transform: "scale(.85)" }}>
        <Toggle
          on={on}
          disabled={disabled}
          ariaLabel={typeof label === "string" ? label : undefined}
        />
      </div>
    </div>
  );
}

PermRow.propTypes = {
  label: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  on: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PermRow.defaultProps = {
  disabled: false,
};

function PermList({ title, children }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.7px] text-ink-3 mb-2">
        {title}
      </div>
      <div className="rec-card !shadow-none divide-y" style={{ padding: "2px 0" }}>
        {children}
      </div>
    </div>
  );
}

PermList.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

function AdminPanel() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["admin"]);
  const [tab, setTab] = useState("users");
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";
  const toggleTheme = () => dispatch(setThemeMode(isDark ? "light" : "dark"));

  // ── Data ──────────────────────────────────────────────────────────────────
  const { users } = useGetAllUsersQuery(
    {},
    { selectFromResult: ({ data }) => ({ users: data && selectAllUsers(data) }) }
  );
  const { permissions } = useGetAllPermissionsQuery(
    {},
    { selectFromResult: ({ data }) => ({ permissions: data && selectAllPermissions(data) }) }
  );
  const { data: emsModules } = useGetEMSModulesQuery();
  const { data: allCommunities } = useGetAllCommunitiesQuery();

  // ── Permissions ───────────────────────────────────────────────────────────
  const adminUsers = users?.filter((u) => u.role?.name === "ADMIN") ?? [];
  const [selectedUserId, setSelectedUserId] = useState("");
  const [updatePermissions] = useUpdatePermissionsByIdMutation();
  const [togglingIds, setTogglingIds] = useState(new Set());

  const handleTogglePermission = async (permission) => {
    if (!selectedUserId || !permission || togglingIds.has(permission.id)) return;
    setTogglingIds((prev) => new Set(prev).add(permission.id));
    try {
      await updatePermissions({
        data: { ...permission, is_active: !permission.is_active },
      }).unwrap();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(permission.id);
        return next;
      });
    }
  };

  const filteredPermissions = Object.fromEntries(
    (permissions ?? []).filter((p) => p.user_id === selectedUserId).map((p) => [p.name, p])
  );
  const userOptions = adminUsers.map((u) => ({ value: u.id, label: `${u.name} · ${u.email}` }));

  // ── Create user ───────────────────────────────────────────────────────────
  const [createType, setCreateType] = useState(CREATE_TYPES.ADMIN);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [communityId, setCommunityId] = useState("");

  const [createCommunityAdmin, { isLoading: isCreatingAdmin }] = useCreateCommunityAdminMutation();
  const [createSuperUser, { isLoading: isCreatingSu }] = useCreateSuperUserMutation();

  const communityOptions = (allCommunities ?? []).map((c) => ({ value: c.id, label: c.name }));
  const isAdmin = createType === CREATE_TYPES.ADMIN;
  const isCreating = isCreatingAdmin || isCreatingSu;
  const canSubmit = isAdmin
    ? userName.trim() && communityId
    : userName.trim() && userEmail.trim() && userPassword.length >= 6;

  const resetForm = () => {
    setUserName("");
    setUserEmail("");
    setUserPassword("");
    setCommunityId("");
  };

  const handleTypeChange = (type) => {
    setCreateType(type);
    resetForm();
  };

  const handleCreate = async () => {
    try {
      if (isAdmin) {
        await createCommunityAdmin({
          communityId,
          name: userName,
          email: userEmail || null,
          has_dashboard_access: Boolean(userEmail.trim()),
        }).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("notifications.admin_created_title"),
            description: t("notifications.admin_created_ok"),
          })
        );
      } else {
        await createSuperUser({
          name: userName,
          email: userEmail,
          password: userPassword,
        }).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("notifications.created_title"),
            description: t("notifications.created_ok"),
          })
        );
      }
      resetForm();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rec-comm-screen">
      {/* ── Full-screen header (design's comm-top) ────────────── */}
      <header className="rec-comm-top">
        <BrandLogo variant="v2" height={56} forceWhite={isDark} />
        <div className="w-px h-[28px] bg-border ml-[6px] mr-[10px]" aria-hidden="true" />
        <Button variant="ghost" size="sm" onClick={() => navigate("/communities")}>
          <Icon name="arrow_back" />
          {t("header.back")}
        </Button>
        <div className="w-px h-[28px] bg-border mx-[6px]" aria-hidden="true" />
        <div className="flex items-center gap-[10px]">
          <div
            className="rec-kpi-ic rec-tint-teal"
            style={{ width: 34, height: 34, borderRadius: 10 }}
          >
            <Icon name="admin_panel_settings" fill />
          </div>
          <div className="leading-[1.2]">
            <div className="text-[14px] font-bold text-ink">{t("header.title")}</div>
            <div className="text-[11px] text-ink-3">{t("header.subtitle")}</div>
          </div>
        </div>
        <div className="flex-1" />
        <button
          type="button"
          className="rec-icon-btn"
          onClick={toggleTheme}
          title={t("header.theme_toggle")}
        >
          <Icon name={isDark ? "light_mode" : "dark_mode"} fill />
        </button>
      </header>

      <div className="rec-comm-body">
        {/* ── Tab nav (design's admin-hnav) ─────────────────────── */}
        <nav className="rec-anchor-nav rec-admin-hnav">
          {TABS.map((tb) => (
            <a
              key={tb.id}
              href={`#${tb.id}`}
              className={tab === tb.id ? "on" : ""}
              onClick={(e) => {
                e.preventDefault();
                setTab(tb.id);
              }}
            >
              <Icon name={tb.icon} />
              {t(tb.labelKey)}
            </a>
          ))}
        </nav>

        {tab === "data" && <ReferenceDataSection />}

        {tab === "users" && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-rec mb-rec">
              {/* ── Create user ─────────────────────────────────────── */}
              <div className="rec-card rec-card-pad">
                <h3 className="rec-section-title" style={{ marginBottom: 16 }}>
                  <Icon name={isAdmin ? "admin_panel_settings" : "shield"} />
                  {t("create_user_title")}
                </h3>
                <Segmented
                  className="w-full mb-4 [&>button]:flex-1"
                  value={createType}
                  options={[
                    { value: CREATE_TYPES.ADMIN, label: t("create_admin_submit") },
                    { value: CREATE_TYPES.SUPER_USER, label: t("create_superuser_title") },
                  ]}
                  onChange={handleTypeChange}
                />
                <div className="flex flex-col gap-[14px]">
                  <FieldText label={t("form.name")} value={userName} onChange={setUserName} />
                  <FieldText
                    label={isAdmin ? t("form.email") : t("form.email_required")}
                    type="email"
                    value={userEmail}
                    onChange={setUserEmail}
                  />
                  {!isAdmin && (
                    <FieldText
                      label={t("form.password")}
                      type="password"
                      value={userPassword}
                      onChange={setUserPassword}
                    />
                  )}
                  {isAdmin && (
                    <FieldSearchSelect
                      label={t("create_admin_community")}
                      value={communityId}
                      options={[{ value: "", label: "—" }, ...communityOptions]}
                      onChange={setCommunityId}
                    />
                  )}
                </div>
                <div className="rec-divider" />
                <div className="flex justify-end">
                  <Button disabled={!canSubmit || isCreating} onClick={handleCreate}>
                    <Icon name={isAdmin ? "admin_panel_settings" : "shield"} />
                    {isAdmin ? t("create_admin_submit") : t("form.submit")}
                  </Button>
                </div>
              </div>

              {/* ── Permissions ─────────────────────────────────────── */}
              <div className="rec-card rec-card-pad">
                <h3 className="rec-section-title" style={{ marginBottom: 16 }}>
                  <Icon name="manage_accounts" />
                  {t("permissions_title")}
                </h3>
                <FieldSearchSelect
                  label={t("select_user")}
                  value={selectedUserId}
                  options={[{ value: "", label: "—" }, ...userOptions]}
                  onChange={setSelectedUserId}
                />
                {!selectedUserId ? (
                  <div
                    className="flex flex-col items-center gap-2 text-ink-3 mt-4"
                    style={{
                      padding: "34px 0",
                      border: "1px dashed var(--border-strong)",
                      borderRadius: "var(--radius)",
                    }}
                  >
                    <Icon name="manage_accounts" style={{ fontSize: 30, opacity: 0.5 }} />
                    <span className="text-[12.5px]">{t("no_user_selected")}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <PermList title={t("section_modules")}>
                      {Object.entries(MODULE_CONFIG).map(([permKey, cfg]) => {
                        const perm = filteredPermissions[permKey];
                        return (
                          <PermRow
                            key={permKey}
                            label={t(`permissions.${permKey}`, { defaultValue: permKey })}
                            icon={cfg.icon}
                            color={cfg.color}
                            on={!!perm?.is_active}
                            disabled={!perm || togglingIds.has(perm?.id)}
                            onToggle={() => handleTogglePermission(perm)}
                          />
                        );
                      })}
                    </PermList>
                    <PermList title={t("section_ems")}>
                      {(emsModules ?? []).map((mod, idx) => {
                        const permKey = `EMS_${mod.module_type}`;
                        const perm = filteredPermissions[permKey];
                        return (
                          <PermRow
                            key={permKey}
                            label={mod.display_name}
                            icon="bolt"
                            color={EMS_COLORS[idx % EMS_COLORS.length]}
                            on={!!perm?.is_active}
                            disabled={!perm || togglingIds.has(perm?.id)}
                            onToggle={() => handleTogglePermission(perm)}
                          />
                        );
                      })}
                    </PermList>
                  </div>
                )}
              </div>
            </div>

            {/* ── Users ───────────────────────────────────────────────── */}
            <UsersSection users={users ?? []} communities={allCommunities ?? []} />
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
